import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Lock, Search, X, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [q, setQ] = useState(params.get("q") || "");
  const [searchOpen, setSearchOpen] = useState(Boolean(params.get("q")));

  useEffect(() => {
    setQ(new URLSearchParams(location.search).get("q") || "");
  }, [location.search]);

  const submit = (e) => {
    e.preventDefault();
    const target = q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop";
    navigate(target);
  };

  const clear = () => {
    setQ("");
    if (params.get("q")) navigate(location.pathname);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo" className="font-display font-700 font-bold text-lg tracking-tight shrink-0">
          KICKSGUARD<span className="text-primary">.</span>SUPPLY
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/" data-testid="nav-home-link" className="hover:text-primary transition-colors">Home</Link>
          <Link to="/storage" data-testid="nav-storage-link" className="hover:text-primary transition-colors">Storage & Display</Link>
          <Link to="/care" data-testid="nav-care-link" className="hover:text-primary transition-colors">Cleaning & Care</Link>
          <Link to="/shop" data-testid="nav-shop-link" className="hover:text-primary transition-colors">Shop All</Link>
        </nav>
        <div className="flex items-center gap-3">
          <form onSubmit={submit} className={`flex items-center border border-border transition-all ${searchOpen ? "w-56" : "w-9"} h-9 overflow-hidden`}>
            <button
              type="button"
              data-testid="header-search-toggle"
              aria-label="Search products"
              onClick={() => setSearchOpen((v) => !v)}
              className="shrink-0 w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search size={16} />
            </button>
            {searchOpen && (
              <>
                <input
                  autoFocus
                  data-testid="header-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search boxes, cleaners…"
                  className="w-full h-full px-1 text-sm outline-none bg-transparent"
                />
                {q && (
                  <button
                    type="button"
                    data-testid="header-search-clear"
                    aria-label="Clear search"
                    onClick={clear}
                    className="shrink-0 w-8 h-9 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </>
            )}
          </form>
          <Link to="/admin" data-testid="admin-link" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Admin">
            <Lock size={18} />
          </Link>
          <Link
            to={user ? "/account" : "/login"}
            data-testid={user ? "account-link" : "signin-link"}
            aria-label={user ? "My account" : "Sign in"}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary transition-colors"
          >
            <User size={16} />
            <span className="hidden md:inline">{user ? (user.name?.split(" ")[0] || "Account") : "Sign In"}</span>
          </Link>
          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 bg-foreground text-white px-4 py-2 text-sm font-medium hover:bg-primary transition-colors"
          >
            <ShoppingBag size={16} />
            Cart
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-primary text-white text-xs w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};
